import { useState } from "react";
import { motion } from "framer-motion";
import { Calendar, Clock, Building2, ArrowRight, CheckCircle, Mail, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPIC_TYPES = [
  { value: "contact-center", label: "Contact Center Modernization" },
  { value: "voice-ai", label: "Voice AI & Predictive Routing" },
  { value: "llm-rag", label: "LLM/RAG Architecture" },
  { value: "gen-ai-strategy", label: "Gen AI Strategy & Roadmapping" },
  { value: "agentic-ai", label: "Agentic AI Systems" },
  { value: "automation", label: "AI Automation" },
  { value: "other", label: "Other AI Topics" }
];

const TIME_SLOTS = [
  { value: "09:00", label: "9:00 AM" },
  { value: "10:00", label: "10:00 AM" },
  { value: "11:00", label: "11:00 AM" },
  { value: "13:00", label: "1:00 PM" },
  { value: "14:00", label: "2:00 PM" },
  { value: "15:00", label: "3:00 PM" },
  { value: "16:00", label: "4:00 PM" }
];

export default function BookingPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    service_type: "",
    preferred_date: "",
    preferred_time: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.company || !formData.service_type || !formData.preferred_date || !formData.preferred_time) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/consultations`, formData);
      setIsSubmitted(true);
      toast.success("Request submitted! Looking forward to our conversation.");
    } catch (error) {
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get minimum date (today)
  const today = new Date().toISOString().split('T')[0];

  if (isSubmitted) {
    return (
      <div className="pt-20" data-testid="booking-page">
        <section className="py-24 md:py-32 hero-gradient grain min-h-[60vh] flex items-center">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="max-w-2xl mx-auto text-center"
            >
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8">
                <CheckCircle className="text-green-600" size={40} />
              </div>
              <h1 className="font-display text-3xl md:text-4xl font-bold text-zinc-950 mb-4">
                Request Submitted!
              </h1>
              <p className="text-lg text-zinc-600 mb-8">
                Thank you for reaching out! I'm excited to connect and exchange ideas. I'll review your request and get back to you within 24 hours.
              </p>
              <div className="p-6 bg-white/80 backdrop-blur rounded-sm border border-zinc-200">
                <p className="text-sm text-zinc-500 mb-2">A confirmation will be sent to:</p>
                <p className="font-semibold text-zinc-950">{formData.email}</p>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="pt-20" data-testid="booking-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 hero-gradient grain">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-royal-blue-800 mb-4 block">
              AI Conversations
            </span>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-zinc-950 mb-6">
              Let's Talk AI
            </h1>
            <p className="text-lg text-zinc-600 leading-relaxed">
              I enjoy connecting with leaders exploring AI. Schedule a time to exchange ideas and perspectives.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Form Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="booking-form-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-2"
            >
              <h2 className="font-display text-2xl font-semibold text-zinc-950 mb-6">
                Schedule an AI Conversation
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="booking-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                      <Input
                        id="name"
                        name="name"
                        placeholder="Your name"
                        value={formData.name}
                        onChange={handleChange}
                        className="bg-zinc-50 border-zinc-200 pl-10"
                        data-testid="booking-name-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={handleChange}
                        className="bg-zinc-50 border-zinc-200 pl-10"
                        data-testid="booking-email-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company">Company / Organization *</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                    <Input
                      id="company"
                      name="company"
                      placeholder="Your company name"
                      value={formData.company}
                      onChange={handleChange}
                      className="bg-zinc-50 border-zinc-200 pl-10"
                      data-testid="booking-company-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Topic of Interest *</Label>
                  <Select
                    value={formData.service_type}
                    onValueChange={(value) => handleSelectChange("service_type", value)}
                  >
                    <SelectTrigger className="bg-zinc-50 border-zinc-200" data-testid="booking-service-select">
                      <SelectValue placeholder="Select a topic" />
                    </SelectTrigger>
                    <SelectContent>
                      {TOPIC_TYPES.map((topic) => (
                        <SelectItem key={topic.value} value={topic.value}>
                          {topic.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="preferred_date">Preferred Date *</Label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                      <Input
                        id="preferred_date"
                        name="preferred_date"
                        type="date"
                        min={today}
                        value={formData.preferred_date}
                        onChange={handleChange}
                        className="bg-zinc-50 border-zinc-200 pl-10"
                        data-testid="booking-date-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Preferred Time (EST) *</Label>
                    <Select
                      value={formData.preferred_time}
                      onValueChange={(value) => handleSelectChange("preferred_time", value)}
                    >
                      <SelectTrigger className="bg-zinc-50 border-zinc-200" data-testid="booking-time-select">
                        <Clock className="mr-2 text-zinc-400" size={18} />
                        <SelectValue placeholder="Select a time" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIME_SLOTS.map((slot) => (
                          <SelectItem key={slot.value} value={slot.value}>
                            {slot.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">What would you like to discuss? (optional)</Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Share any topics, challenges, or questions you'd like to explore..."
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="bg-zinc-50 border-zinc-200 resize-none"
                    data-testid="booking-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-royal-blue-800 hover:bg-royal-blue-700 text-white px-8 py-6 text-xs tracking-wider uppercase"
                  data-testid="booking-submit-btn"
                >
                  {isSubmitting ? (
                    "Submitting..."
                  ) : (
                    <>
                      Schedule AI Conversation
                      <ArrowRight className="ml-2" size={16} />
                    </>
                  )}
                </Button>
              </form>
            </motion.div>

            {/* Sidebar Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="sticky top-28">
                <div className="p-8 bg-zinc-950 text-white rounded-sm mb-6">
                  <Calendar className="mb-4 opacity-80" size={32} />
                  <h3 className="font-display text-xl font-semibold mb-3">
                    What to Expect
                  </h3>
                  <ul className="space-y-3 text-zinc-300 text-sm">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="text-royal-blue-400 flex-shrink-0 mt-0.5" size={16} />
                      <span>30-minute conversation</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="text-royal-blue-400 flex-shrink-0 mt-0.5" size={16} />
                      <span>Exchange of ideas and perspectives</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="text-royal-blue-400 flex-shrink-0 mt-0.5" size={16} />
                      <span>Insights from my research and experiments</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="text-royal-blue-400 flex-shrink-0 mt-0.5" size={16} />
                      <span>No obligation - just a friendly chat</span>
                    </li>
                  </ul>
                </div>

                <div className="p-6 bg-zinc-50 border border-zinc-200 rounded-sm">
                  <h4 className="font-semibold text-zinc-950 mb-2">Response Time</h4>
                  <p className="text-sm text-zinc-600">
                    I'll review your request and get back to you within 24 hours via email.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
